import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { CustomSwitch } from "components/common/Switch";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ErrorMessage from "components/common/ErrorMessage";
import { setError, updateType, addType, typesSelector } from "slices/typeSlice";
import { Controller, FormProvider, useForm } from "react-hook-form";
import PhoneInput from "components/common/PhoneInput";
import { useNavigate } from "react-router-dom";
import FullPageLoader from "components/common/FullPageLoader";
import { usePermissions } from "layouts/Admin";
import { AsyncPaginate } from "react-select-async-paginate";
import { selectCustomStyles } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";

const AccountForm = ({ item }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();

  const methods = useForm({});
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    control,
    setValue,
    clearErrors,
    resetField,
  } = methods;

  const { loader, error } = useSelector(typesSelector);
  const [status, setStatus] = useState(1);
  const [vriOnDemandLanguageState, setVriOnDemandLanguageState] = useState([]);
  const [vriPrescheduledLanguageState, setVriPrescheduledLanguageState] = useState([]);

  const resetAllFields = () => {
    setValue("name", "");
    setValue("email", "");
    setValue("password", "");
    setValue("phone", "");
    setVriOnDemandLanguageState([]);
    setVriPrescheduledLanguageState([]);
    setStatus(1);
  };

  useEffect(() => {
    setValue("name", item?.name || "");
    setValue("email", item?.email || "");
    setValue("password", "");
    setValue("phone", item?.phone || "");
    setStatus(typeof item?.status === "number" ? item?.status : 1);

    if (item?.vri_on_demand_language_ids?.length > 0) {
      loadLanguagesByIds(item.vri_on_demand_language_ids).then((options) => {
        setVriOnDemandLanguageState(options);
        setValue("vri_on_demand_language_ids", item.vri_on_demand_language_ids);
      });
    } else {
      setVriOnDemandLanguageState([]);
      setValue("vri_on_demand_language_ids", []);
    }

    if (item?.vri_prescheduled_language_ids?.length > 0) {
      loadLanguagesByIds(item.vri_prescheduled_language_ids).then((options) => {
        setVriPrescheduledLanguageState(options);
        setValue("vri_prescheduled_language_ids", item.vri_prescheduled_language_ids);
      });
    } else {
      setVriPrescheduledLanguageState([]);
      setValue("vri_prescheduled_language_ids", []);
    }

    dispatch(setError({}));
    return () => { resetAllFields(); };
  }, [item]);

  const submitCallback = (success, message) => {
    if (success) {
      toast.success(message, { className: "successToast" });
      resetAllFields();
      navigate(-1);
    } else {
      toast.error(message, { className: "errorToast" });
    }
  };

  const loadLanguages = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true)
    );
    const options = result?.data || (Array.isArray(result) ? result : []);
    return {
      options: options.map((l) => ({ value: l.id, label: l.name })),
      hasMore: page < (result?.last_page || 1),
      additional: { page: page + 1 },
    };
  };

  const loadLanguagesByIds = async (ids) => {
    if (!ids?.length) return [];
    const result = await dispatch(
      fetchLanguages(1, { language_ids: ids, pageLength: ids.length }, true, true)
    );
    const options = result?.data || (Array.isArray(result) ? result : []);
    return options.map((l) => ({ value: l.id, label: l.name }));
  };

  const onSubmit = (data) => {
    dispatch(setError({}));
    const newData = { ...data, status, credentialsSend: 1, notification: 1 };
    if (!item?.id) {
      dispatch(addType(newData, submitCallback));
    } else {
      dispatch(updateType(newData, item?.id, submitCallback));
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form className="card_table p-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="foam_page_row">

            {/* Name */}
            <div className="row-primary">
              <h2>Name *</h2>
              <div className="w-100">
                <Controller
                  name="name"
                  rules={{ required: "Organization name is required" }}
                  control={control}
                  render={({ field }) => (
                    <input className="input-in type_inp" type="text" placeholder="Name" {...field} />
                  )}
                />
                {error?.errors?.name?.map((e, i) => (
                  <div key={i} style={{ marginTop: 10 }}><ErrorMessage text={e} /></div>
                ))}
                {errors?.name?.message && (
                  <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.name.message} /></div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="row-primary">
              <h2>Email *</h2>
              <div className="w-100">
                <Controller
                  name="email"
                  rules={{ required: "Email is required" }}
                  control={control}
                  render={({ field }) => (
                    <input
                      className={`input-in type_inp ${error?.errors?.email ? "border-red" : ""}`}
                      type="email"
                      autoComplete="off"
                      placeholder="Email Address"
                      {...field}
                    />
                  )}
                />
                {error?.errors?.email?.map((e, i) => (
                  <div key={i} style={{ marginTop: 10 }}><ErrorMessage text={e} /></div>
                ))}
                {errors?.email?.message && (
                  <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.email.message} /></div>
                )}
              </div>
            </div>

            {/* Password */}
            <div className="row-primary">
              <h2>Password {!item && "*"}</h2>
              <div className="w-100">
                <Controller
                  name="password"
                  rules={!item ? { required: "Password is required" } : undefined}
                  control={control}
                  render={({ field }) => (
                    <input
                      className="input-in type_inp"
                      autoComplete="off"
                      type="password"
                      placeholder="Password"
                      {...field}
                    />
                  )}
                />
                {error?.errors?.password?.map((e, i) => (
                  <div key={i} style={{ marginTop: 10 }}><ErrorMessage text={e} /></div>
                ))}
                {errors?.password?.message && (
                  <div style={{ marginBottom: 10 }}><ErrorMessage text={errors.password.message} /></div>
                )}
              </div>
            </div>

            {/* Phone */}
            <div className="row-primary">
              <h2>Phone</h2>
              <div className="w-100">
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      className={`input-in type_inp ${error?.errors?.phone ? "border-red" : ""}`}
                      type="tel"
                      placeholder="Phone number"
                      {...field}
                    />
                  )}
                />
                {error?.errors?.phone?.map((e, i) => (
                  <div key={i} style={{ marginTop: 10 }}><ErrorMessage text={e} /></div>
                ))}
              </div>
            </div>

            {/* VRI On Demand Languages */}
            <div className="row-primary mt-4 w-100">
              <h2>VRI on demand languages</h2>
              <Controller
                name="vri_on_demand_language_ids"
                control={control}
                render={({ field }) => (
                  <div style={{ width: "100%" }}>
                    <AsyncPaginate
                      menuPlacement="auto"
                      isMulti
                      styles={selectCustomStyles}
                      value={vriOnDemandLanguageState}
                      loadOptions={loadLanguages}
                      isClearable
                      escapeClearsValue
                      onChange={(selected) => {
                        setVriOnDemandLanguageState(selected);
                        field.onChange(selected.map((opt) => opt.value));
                      }}
                      additional={{ page: 1 }}
                    />
                    {errors?.vri_on_demand_language_ids?.message && (
                      <div style={{ marginBottom: 10 }}>
                        <ErrorMessage text={errors.vri_on_demand_language_ids.message} />
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            {/* VRI Pre-scheduled Languages */}
            <div className="row-primary mt-4 w-100">
              <h2>VRI prescheduled languages</h2>
              <Controller
                name="vri_prescheduled_language_ids"
                control={control}
                render={({ field }) => (
                  <div style={{ width: "100%" }}>
                    <AsyncPaginate
                      menuPlacement="auto"
                      isMulti
                      styles={selectCustomStyles}
                      value={vriPrescheduledLanguageState}
                      loadOptions={loadLanguages}
                      isClearable
                      escapeClearsValue
                      onChange={(selected) => {
                        setVriPrescheduledLanguageState(selected);
                        field.onChange(selected.map((opt) => opt.value));
                      }}
                      additional={{ page: 1 }}
                    />
                    {errors?.vri_prescheduled_language_ids?.message && (
                      <div style={{ marginBottom: 10 }}>
                        <ErrorMessage text={errors.vri_prescheduled_language_ids.message} />
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>

          <div className="Form-footer footer_fullpage">
            <div className="row-primary m-0 align-items-center">
              {checkPermission("Status Client Account") && (
                <>
                  <h2 className="mt-0">Status</h2>
                  <CustomSwitch
                    labelText="active"
                    checked={status === 1}
                    handleChange={(checked) => setStatus(checked ? 1 : 0)}
                  />
                </>
              )}
            </div>
            <SubmitButton loader={loader} btnText={item?.id ? "Update" : "Create"} />
          </div>
        </form>
      </FormProvider>

      {loader && <FullPageLoader />}
    </>
  );
};

export default AccountForm;
